import React from 'react';
import { PlaybookTransformItem, PlaybookMapping, PlaybookEnrichment, PlaybookEventConfig } from '@/types/playbook';
import { UnifiedSchema, EventSchema } from '@/types/schema';
import { findParameterDataTypeInSchema } from '@/utils/schemaUtils';
import { EditorSelection } from '../PlaybookEditorTab';

// Import individual transform editors
import EnrichmentEditor from '@components/playbooks/transforms/EnrichmentEditor';
import MappingEditor from '@components/playbooks/transforms/MappingEditor';
import ExpressionEditor from '@components/playbooks/transforms/ExpressionEditor';
import SpreadingEditor from '@components/playbooks/transforms/SpreadingEditor';
import FilterEditorEnhanced from '@components/playbooks/transforms/FilterEditorEnhanced';

interface PlaybookTransformEditorProps {
  selectedTransform: PlaybookTransformItem | null;
  onUpdateTransform: (transformId: string, updates: Partial<PlaybookTransformItem>) => void;
  onTransformSelect: (transformId: string) => void;
  inputSchema: UnifiedSchema | null;
  outputSchema: UnifiedSchema | null;
  allEvents: EventSchema[];
  uiEventConfigs: PlaybookEventConfig[];
  getUsedInputProperties: () => string[];
  getUsedOutputProperties: () => string[];
  onSelectionChange: (selection: EditorSelection) => void;
  allUiTransforms: PlaybookTransformItem[];
  allowUndefinedTransforms: boolean;
  isPropertyPairApplicableToAllEvents: (inputPath: string | undefined, outputPath: string | undefined) => boolean; // Add this prop
}

const PlaybookTransformEditor: React.FC<PlaybookTransformEditorProps> = ({
  selectedTransform,
  onUpdateTransform,
  onTransformSelect,
  inputSchema,
  outputSchema,
  allEvents,
  uiEventConfigs,
  getUsedInputProperties,
  getUsedOutputProperties,
  isPropertyPairApplicableToAllEvents, // Destructure this prop
}) => {
  if (!selectedTransform) return null;

  const commonProps = {
    transform: selectedTransform,
    onUpdate: (updates: Partial<PlaybookTransformItem>) => {
      const updatedTransform: Partial<PlaybookTransformItem> = { ...updates };

      // Synchronize inputPath with content.inputKey if content is updated
      if (updates.content && (updates.content as PlaybookMapping)?.inputKey !== undefined) {
        updatedTransform.inputPath = (updates.content as PlaybookMapping).inputKey;
      }
      // Synchronize outputPath with content.outputKey if content is updated
      if (updates.content && ((updates.content as PlaybookMapping)?.outputKey !== undefined || (updates.content as PlaybookEnrichment)?.outputKey !== undefined)) {
        updatedTransform.outputPath = (updates.content as PlaybookMapping)?.outputKey || (updates.content as PlaybookEnrichment)?.outputKey;
      }

      // Only recalculate inputType and outputType if they weren't explicitly provided in the updates
      // This prevents overwriting preserved type information during scope changes
      const currentInputPath = updatedTransform.inputPath;
      if (updates.inputType === undefined) {
        // Only recalculate if no explicit type was provided
        if (currentInputPath !== undefined && currentInputPath !== null && currentInputPath !== '') {
          const calculatedInputType = inputSchema ? findParameterDataTypeInSchema(inputSchema, currentInputPath) : '';
          updatedTransform.inputType = calculatedInputType;
        } else {
          updatedTransform.inputType = undefined;
        }
      }
      
      const currentOutputPath = updatedTransform.outputPath;
      if (updates.outputType === undefined) {
        // Only recalculate if no explicit type was provided
        if (currentOutputPath !== undefined && currentOutputPath !== null && currentOutputPath !== '') {
          const calculatedOutputType = outputSchema ? findParameterDataTypeInSchema(outputSchema, currentOutputPath) : '';
          updatedTransform.outputType = calculatedOutputType;
        } else {
          updatedTransform.outputType = undefined;
        }
      }

      onUpdateTransform(selectedTransform.id, updatedTransform);

      // If scope changes from global to eventSpecific, update selection to show the event view
      // This logic might need to be handled at a higher level (PlaybookTransformEditorPanel)
      // as it involves changing the overall selection state. For now, keeping it here.
      if (selectedTransform.appliesToScope === 'global' && updates.appliesToScope === 'eventSpecific') {
        if (allEvents.length > 0) {
          // This requires onSelectionChange from the parent, which is not passed here.
          // This part of the logic should be moved to PlaybookTransformEditorPanel.
          console.warn("Scope change logic for selection not fully implemented in PlaybookTransformEditor.");
        }
      } else if (selectedTransform.appliesToScope === 'eventSpecific' && updates.appliesToScope === 'global') {
        // This requires onSelectionChange from the parent.
        console.warn("Scope change logic for selection not fully implemented in PlaybookTransformEditor.");
      }
    },
    inputSchema: inputSchema,
    outputSchema: outputSchema,
    allEvents: allEvents,
    uiEventConfigs: uiEventConfigs,
    isPropertyPairApplicableToAllEvents: isPropertyPairApplicableToAllEvents, // Pass this prop
  };

  switch (selectedTransform.type) {
    case 'enrichment':
      return <EnrichmentEditor 
        {...commonProps} 
        availableProperties={outputSchema?.parameters || []}
        usedOutputProperties={getUsedOutputProperties()}
        onCloseEditor={() => {
          // Only set mappingType if it's a new transform
          if (selectedTransform.isNew) {
            onUpdateTransform(selectedTransform.id, {
              mappingType: 'manual',
              isNew: false
            });
          }
          onTransformSelect('');
        }}
      />;
    case 'mapping':
      return <MappingEditor 
        {...commonProps} 
        availableProperties={inputSchema?.parameters || []} // Input key should use input schema
        usedInputProperties={getUsedInputProperties()}
        usedOutputProperties={getUsedOutputProperties()}
        onCloseEditor={() => {
          // Only set mappingType if it's a new transform
          if (selectedTransform.isNew) {
            onUpdateTransform(selectedTransform.id, {
              mappingType: 'manual',
              isNew: false
            });
          }
          onTransformSelect('');
        }}
      />;
    case 'expression':
      return <ExpressionEditor
        {...commonProps}
        outputSchema={outputSchema}
        onCloseEditor={() => {
          // Only set mappingType if it's a new transform
          if (selectedTransform.isNew) {
            onUpdateTransform(selectedTransform.id, {
              mappingType: 'manual',
              isNew: false
            });
          }
          onTransformSelect('');
        }}
      />;
    case 'spreading':
      return <SpreadingEditor 
        {...commonProps} 
        availableProperties={inputSchema?.parameters || []} // Input key should use input schema
        usedInputProperties={getUsedInputProperties()}
        allEvents={allEvents}
        onCloseEditor={() => {
          // Only set mappingType if it's a new transform
          if (selectedTransform.isNew) {
            onUpdateTransform(selectedTransform.id, {
              mappingType: 'manual',
              isNew: false
            });
          }
          onTransformSelect('');
        }}
      />;
    case 'filter':
      return <FilterEditorEnhanced 
        {...commonProps} 
        allEvents={allEvents}
        onCloseEditor={() => {
          // Only set mappingType if it's a new transform
          if (selectedTransform.isNew) {
            onUpdateTransform(selectedTransform.id, {
              mappingType: 'manual',
              isNew: false
            });
          }
          onTransformSelect('');
        }}
      />;
    default:
      return (
        <div className="p-4 text-center text-gray-500 dark:text-gray-400">
          Unknown transform type: {selectedTransform.type}
        </div>
      );
  }
};

export default PlaybookTransformEditor;
